import { useContext } from 'react'

import { datadogLogs } from '@datadog/browser-logs'
import { OptimizelyContext } from '@optimizely/react-sdk'
import Cookies from 'js-cookie'

import { getAuthToken } from '@library/auth'
import { canUseWindow } from '@library/environment'

/**
 * Feature flag hook to check if Category Comprehension experiment is enabled
 * Uses Optimizely experiment: beetroots_ofx_category_comprehension
 *
 * @returns {boolean} true if the Category Comprehension treatment should be shown
 */
export const useIsCategoryComprehensionEnabled = (): boolean => {
  const { optimizely } = useContext(OptimizelyContext)

  if (!canUseWindow()) {
    return false
  }

  const authToken = getAuthToken()
  const isAuthenticated = !!authToken

  let hasAcceptedAnalytics = false
  try {
    const consentCookieString = Cookies.get('v1_gousto_cookie_consent')
    const consentCookie = consentCookieString ? JSON.parse(consentCookieString) : {}
    hasAcceptedAnalytics = consentCookie?.categories?.includes('ANALYTICS')
  } catch (error) {
    let errorMessage = 'Unknown error when parsing cookie consent'
    if (error instanceof Error) {
      errorMessage = error.message
      datadogLogs.logger.error('Error parsing CategoryComprehension cookie consent', {
        error: errorMessage,
        cookieConsent: Cookies.get('v1_gousto_cookie_consent'),
      })
    }
  }

  // We only allocate users to the experiment if they have consented
  // to the Analytics cookies and are not authenticated (unless bypassed)
  let enabled = false
  let variationKey: string | null | undefined

  if (optimizely && hasAcceptedAnalytics && !isAuthenticated) {
    try {
      const decision = optimizely.decide('beetroots_ofx_category_comprehension')
      enabled = decision.enabled
      variationKey = decision.variationKey
    } catch (error) {
      datadogLogs.logger.error('Error deciding category comprehension', {
        error: error instanceof Error ? error.message : 'Unknown error',
      })
    }
  }

  const isEnabled = enabled && variationKey === 't1'

  return isEnabled
}
